import { useCallback, useEffect, useState } from "react";
import { Bookmark, X } from "lucide-react";
import { api } from "../lib/api";
import { readSelection, writeSelection } from "../lib/selections";

const STORE = "kskf-lease-saved";


const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "—");
const read = () => readSelection(STORE, []);
const idOf = (s) => `${s.county}|${s.county_b || ""}|${s.acres}|${s.location}`;

/**
 * A funder's shortlist of comparisons, kept in this browser. Each saved line is re-priced from the API when it
 * is opened, so a shortlist made in June cannot quietly go stale.
 */
export const SavedComparisons = ({ current, onOpen }) => {
  const [saved, setSaved] = useState(read);
  const [rows, setRows] = useState({});

  const persist = (next) => {
    setSaved(next);
    writeSelection(STORE, next);
  };

  const load = useCallback(async (s) => {
    const q = new URLSearchParams({
      county: s.county, acres: String(s.acres), location: s.location,
      ...(s.county_b ? { county_b: s.county_b } : {}),
    }).toString();
    try {
      const r = await api.get(`/lease-comparison?${q}`);
      const col = r.data.columns[0];
      setRows((prev) => ({
        ...prev,
        [idOf(s)]: {
          freehold: col.freehold_shillings,
          lease15: col.rows.find((x) => x.term === "lease_15")?.day_one_shillings,
          rent: col.rows.find((x) => x.term === "lease_99")?.rent_year_shillings,
          cheapest: col.rows.find((x) => x.term === col.cheapest_over_15)?.term_label,
        },
      }));
    } catch {
      // a saved line that cannot be priced simply shows dashes
    }
  }, []);

  useEffect(() => { saved.forEach(load); }, [saved, load]);

  const isSaved = saved.some((s) => idOf(s) === idOf(current));

  return (
    <section className="mt-20" data-testid="saved-comparisons">
      <p className="eyebrow">Your shortlist</p>
      <h2 className="display-3 mt-4">Keep The Ones Worth Coming Back To</h2>
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
        Save any comparison and it stays in this browser. Every saved line is re-priced from the live model when
        this page loads, so a shortlist you made weeks ago is never quoting old numbers.
      </p>

      <button
        type="button"
        onClick={() => persist(isSaved ? saved.filter((s) => idOf(s) !== idOf(current)) : [...saved, current])}
        data-testid="saved-comparisons-save"
        className={`mono mt-7 inline-flex items-center gap-2 border px-5 py-3 text-[0.6rem] uppercase tracking-[0.16em] transition-colors ${
          isSaved ? "border-ochre bg-ochre text-black" : "border-ochre text-ochre hover:bg-ochre hover:text-black"
        }`}
      >
        <Bookmark size={13} /> {isSaved ? "Saved — tap to remove" : "Save this comparison"}
      </button>

      {saved.length ? (
        <div className="mt-8 overflow-x-auto border border-zinc-900">
          <table className="w-full min-w-[860px] text-left" data-testid="saved-comparisons-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                {["Saved comparison", "Freehold", "15-year lease", "Rent a year", "Cheapest route", ""].map((h) => (
                  <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {saved.map((s) => {
                const r = rows[idOf(s)] || {};
                const testId = `${s.county}-${s.acres}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <tr key={idOf(s)} className="border-b border-zinc-900" data-testid={`saved-row-${testId}`}>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => onOpen(s)}
                        data-testid={`saved-open-${testId}`}
                        className="text-left font-display text-sm uppercase tracking-tight text-white hover:text-ochre"
                      >
                        {s.county}{s.county_b ? ` v ${s.county_b}` : ""} · {s.acres} acres
                      </button>
                      <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">
                        {s.location_label || s.location}
                      </span>
                    </td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{ksh(r.freehold)}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{ksh(r.lease15)}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{ksh(r.rent)}</td>
                    <td className="mono px-4 py-4 text-xs text-ochre">{r.cheapest || "—"}</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => persist(saved.filter((x) => idOf(x) !== idOf(s)))}
                        data-testid={`saved-remove-${testId}`}
                        className="text-zinc-500 transition-colors hover:text-maasai"
                        aria-label={`Remove ${s.county}`}
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mono mt-8 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-ochre"
          data-testid="saved-comparisons-empty">
          Nothing saved yet. Set a county and an acreage above, then save it and build a shortlist.
        </p>
      )}
    </section>
  );
};

export default SavedComparisons;
